import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppApiService, convertApiItemsResponseToItems } from '@shared/core';
import { DEFAULT_REQUEST_PARAMS, EntityId, EntityUuid } from '@shared/core/types';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { CalendarService } from './wfm-calendar.service';
import {
  ActivitySaveRequest,
  CancelIntroLessonRequest,
  DeleteScheduleCheckResponse,
  DutyData,
  DutyType,
  EventData,
  EventType,
  EventTypesResponse,
  GroupActivitiesResponse,
  ManagerAllGroupsResponse,
  ManagerGroupsResponse,
  ManagerHierarchyGroupsResponse,
  OperatorItem,
  OperatorMertics,
  Reason,
  ScheduleData,
  ServiceItem,
  ServiceItemsResponse,
} from '../models/types';
import { WfmBaseApiService } from '@shared/core/services/wfm-base-api.service';
import { Item } from '../models/interfaces';

@Injectable({
  providedIn: 'root',
})
export class WfmApiService extends WfmBaseApiService {
  constructor(protected api: AppApiService, private calendarService: CalendarService) {
    super(api);
  }

  getSchedule(operatorId: EntityId, dateInterval: Interval): Observable<ScheduleData> {
    return this.api
      .get<ScheduleData>(`${this.baseEndpoint}/api/user/operators/${operatorId}/schedule`, {
        params: {
          startDate: this.calendarService.toUTCFormatFromServerDateTime(new Date(dateInterval.start)),
          endDate: this.calendarService.toUTCFormatFromServerDateTime(new Date(dateInterval.end)),
        },
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(catchError(() => of(null)));
  }

  getGroupActivities(groups: EntityUuid[], start: Date, end: Date): Observable<GroupActivitiesResponse> {
    return this.api
      .get<GroupActivitiesResponse>(`${this.baseEndpoint}/api/user/operators/group-activities`, {
        params: {
          groups: groups.join(','),
          startDate: this.calendarService.toUTCFormatFromServerDateTime(new Date(start)),
          endDate: this.calendarService.toUTCFormatFromServerDateTime(new Date(end)),
        },
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(catchError(() => of(null)));
  }

  getDutyTypes(): Observable<DutyType[]> {
    return this.api
      .get<{ items: DutyType[] }>(`${this.baseEndpoint}/api/settings/operators/dutyTypes`, {
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(
        map(({ items }) => items),
        shareReplay({ refCount: false, bufferSize: 1 }),
      );
  }

  addWorkingHours(operatorId: EntityId, schedule: DutyData): Observable<DutyData> {
    return this.api.post<DutyData>(`${this.baseEndpoint}/api/user/operators/${operatorId}/working-hours`, {
      ...DEFAULT_REQUEST_PARAMS,
      params: schedule,
    });
  }

  updateWorkingHours(operatorId: EntityId, schedule: DutyData, onlyForOneDate: boolean): Observable<DutyData> {
    return this.api.put<DutyData>(
      `${this.baseEndpoint}/api/user/operators/${operatorId}/working-hours/${schedule.uuid}`,
      {
        ...DEFAULT_REQUEST_PARAMS,
        params: {
          ...schedule,
          onlyForOneDate,
        },
      },
    );
  }

  deleteWorkingHours(operatorId: EntityId, scheduleUuid: EntityUuid, onlyForOneDate: boolean): Observable<void> {
    return this.api.delete<void>(
      `${this.baseEndpoint}/api/user/operators/${operatorId}/working-hours/${scheduleUuid}`,
      {
        ...DEFAULT_REQUEST_PARAMS,
        params: {
          onlyForOneDate: onlyForOneDate,
        },
      },
    );
  }

  createActivity(data: ActivitySaveRequest): Observable<EventData> {
    const operatorId = data.operatorsIds[0];

    return this.api
      .post<any>(`${this.baseEndpoint}/api/user/operators/${operatorId}/activities`, {
        ...DEFAULT_REQUEST_PARAMS,
        params: data,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  createGroupActivity(data: ActivitySaveRequest): Observable<EventData> {
    return this.api
      .post<any>(`${this.baseEndpoint}/api/user/operators/activities/group/`, {
        ...DEFAULT_REQUEST_PARAMS,
        params: data,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  updateActivity(operatorId: EntityId, activityUuid: EntityUuid, params: ActivitySaveRequest): Observable<void> {
    return this.api
      .put<any>(`${this.baseEndpoint}/api/user/operators/${operatorId}/activities/${activityUuid}`, {
        params,
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  updateGroupActivity(groupUuid: EntityUuid, params: ActivitySaveRequest): Observable<void> {
    return this.api
      .put<any>(`${this.baseEndpoint}/api/user/operators/activities/group/${groupUuid}`, {
        params,
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  deleteActivity(operatorId: EntityId, activityUuid: EntityUuid, onlyForOneDate: boolean): Observable<void> {
    return this.api
      .delete<any>(`${this.baseEndpoint}/api/user/operators/${operatorId}/activities/${activityUuid}`, {
        ...DEFAULT_REQUEST_PARAMS,
        params: {
          onlyForOneDate,
        },
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  deleteGroupActivity(groupUuid: EntityUuid, activity: EventData, onlyForOneDate: boolean): Observable<void> {
    return this.api
      .delete<any>(`${this.baseEndpoint}/api/user/operators/activities/group/${groupUuid}`, {
        ...DEFAULT_REQUEST_PARAMS,
        params: {
          ...activity,
          onlyForOneDate,
        },
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error.error)));
  }

  getEventTypes(): Observable<EventType[]> {
    return this.api.get<EventTypesResponse>(`${this.baseEndpoint}/api/settings/operators/eventTypes`).pipe(
      convertApiItemsResponseToItems,
      catchError(() => of(null)),
    );
  }

  getManagerAllGroups(): Observable<ManagerAllGroupsResponse> {
    return this.api.get<ManagerAllGroupsResponse>(`${this.baseEndpoint}/api/user/operators/manager/all-groups`, {
      ...DEFAULT_REQUEST_PARAMS,
      params: {},
    });
  }

  getManagerHierarchyGroups(): Observable<ManagerHierarchyGroupsResponse> {
    return this.api.get<ManagerHierarchyGroupsResponse>(
      `${this.baseEndpoint}/api/user/operators/manager/hierarchy-groups`,
      {
        ...DEFAULT_REQUEST_PARAMS,
        params: {},
      },
    );
  }

  getManagerGroups(groups: Item[], dateInterval: Interval): Observable<ManagerGroupsResponse> {
    return this.api.get<ManagerGroupsResponse>(`${this.baseEndpoint}/api/v2/group/metrics/days`, {
      ...DEFAULT_REQUEST_PARAMS,
      params: {
        groups: groups.map(group => group.uuid).join(','),
        startDate: this.calendarService.toISOStringSafe(dateInterval.start),
        endDate: this.calendarService.toISOStringSafe(dateInterval.end),
      },
    });
  }

  cancelIntroLesson(operatorId: EntityId, activityUuid: string, data: CancelIntroLessonRequest): Observable<void> {
    return this.api.delete<void>(`${this.baseEndpoint}/api/user/operators/${operatorId}/activities/${activityUuid}`, {
      ...DEFAULT_REQUEST_PARAMS,
      params: data,
    });
  }

  getStudentServices(studentId: number): Observable<ServiceItem[] | null> {
    return this.api
      .get<ServiceItemsResponse>(
        `${this.baseEndpoint}/api/user/get-student-education-services/${studentId}`,
        DEFAULT_REQUEST_PARAMS,
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => of(null)),
      );
  }

  getReasons(uuid: string, educationServiceId: number): Observable<Reason[]> {
    return this.api.get<Reason[]>(`${this.baseEndpoint}/api/slot/absence-reasons`, {
      params: { uuid, educationServiceId },
      ...DEFAULT_REQUEST_PARAMS,
    });
  }

  getUser(id: number): Observable<OperatorItem> {
    return this.api.get<OperatorItem>(`${this.baseEndpoint}/api/user/operators/${id}`, DEFAULT_REQUEST_PARAMS);
  }

  getMetrics(params: Object): Observable<OperatorMertics> {
    return this.api.get<OperatorMertics>(`${this.baseEndpoint}/api/v2/group/metrics/month`, {
      ...DEFAULT_REQUEST_PARAMS,
      params,
    });
  }

  deleteScheduleCheck(operatorId: EntityId, schedule: DutyData): Observable<DeleteScheduleCheckResponse> {
    return this.api.delete<DeleteScheduleCheckResponse>(
      `${this.baseEndpoint}/api/user/operators/${operatorId}/working-hours/${schedule.uuid}/check`,
      DEFAULT_REQUEST_PARAMS,
    );
  }
}
